import * as THREE from 'three'

import utils from '@/utils/three'
import { toDegrees } from '@/utils/math'
import { promisify } from '@/utils/immutable'
import { createTileSphere } from '@/backends/TileSphere'

import { createSphereShader } from '@/values/shaders'

const COMPUTATIONAL_POLYGONS = 96
const INITIAL_ZOOM = 1
const MAX_ZOOM = 4
const SPHERE_WIDTH = 128
const SPHERE_HEIGHT = 128

export default ({ 
  container,

  image,
  initialFov, 
  radius, 
  aspect, 
  sight,
  antialiasing,

  buildTileUrlV3,
  tileImage,

  w,
  h,

  onTileLoading
}) => {
  const state = {
    camera: null,
    scene: null,
    geometry: null,
    texture: null,
    material: null,
    mesh: null,
    wireframe: null,
    lines: null,
    grid: null,
    raycaster: null,
    frustum: null,
    frustumMatrix: null,
    theoreticalMesh: null
  }

  let lat, lon
  
  let tiler
  let layers

  let resizeObserver

  const listeners = {}

  const makeComplexGeometryMesh = faces => {
    const complexGeometry = new THREE.SphereGeometry(radius, faces, faces)
    complexGeometry.scale(-1, 1, 1)

    return new THREE.Mesh(complexGeometry, new THREE.MeshBasicMaterial())
  }

  const emit = (event, props) => {
    (listeners[event] || []).forEach(f => f(props))
  }

  const on = (k, f) => {
    k && f && (listeners[k] = [...listeners[k] || [], f])
  }

  return {
    create() {
      const renderer = new THREE.WebGLRenderer({antialias: antialiasing})
      renderer.setPixelRatio(window.devicePixelRatio)
      renderer.setSize(w(), h())

      container.appendChild(renderer.domElement)

      resizeObserver = new ResizeObserver(() => {
        renderer.setSize(w(), h())
        
        if (state.camera) {
          state.camera.aspect = aspect()
          state.camera.updateProjectionMatrix()
        }
      }).observe(container)

      return {
        renderer
      }
    },

    async init({ configuration }) {
      state.camera = new THREE.PerspectiveCamera(initialFov, aspect(), 1, 3 * radius)
      state.camera.position.set(0, 0, 0)
      state.camera.target = new THREE.Vector3(0, 0, 0)

      state.scene = new THREE.Scene()

      false && is([
        [new THREE.Vector3(1, 0, 0), 0xff0000],
        [new THREE.Vector3(0, 1, 0), 0x00ff00],
        [new THREE.Vector3(0, 0, 1), 0x0000ff]
      ]).map(([axis, color]) => {
        state.scene.add(new THREE.ArrowHelper(axis.normalize(), new THREE.Vector3(0, 0, 0), 10, color))
      })

      const tiled = configuration.panorama_tile_enabled

      if (!tiled) {
        state.geometry = new THREE.SphereGeometry(radius, SPHERE_WIDTH, SPHERE_HEIGHT)
        state.geometry.scale(-1, 1, 1)

        state.texture = await promisify((resolve) => utils.texture(image.storage_url, texture => resolve(texture)))

        emit('onImageApplied', { 
          imageWidth: state.texture.image.width, 
          imageHeight: state.texture.image.height, 
          imageMaxWidth: state.texture.image.width, 
          imageMaxHeight: state.texture.image.height 
        })

        state.material = new THREE.ShaderMaterial(createSphereShader({ texture: state.texture, radius }))

        state.mesh = new THREE.Mesh(state.geometry, state.material)
        state.scene.add(state.mesh)

        emit('onMeshChanged', state.mesh)

        state.wireframe = new THREE.EdgesGeometry(state.geometry)
        state.lines = new THREE.LineBasicMaterial({
          ...utils.TRANSPARENCY_MIXIN,
          linewidth: 1,
          visible: false
        })

        state.grid = new THREE.LineSegments(state.wireframe, state.lines)
        state.mesh.add(state.grid)
      }

      state.raycaster = new THREE.Raycaster()
      state.frustum = new THREE.Frustum()
      state.frustumMatrix = new THREE.Matrix4()
      state.theoreticalMesh = makeComplexGeometryMesh(COMPUTATIONAL_POLYGONS)

      lat = 0
      lon = 0

      layers = {}

      tiled && (tiler = createTileSphere({
        maxZoom: MAX_ZOOM,

        buildTileUrlV3,
        tileImage,

        onLayerAdded: ({ zoom, texture, mesh: textureMesh, imageWidth, imageHeight, imageMaxWidth, imageMaxHeight }) => {
          const radiusWithOffset = radius + zoom * -10

          //console.log(radiusWithOffset)

          const material = texture && new THREE.ShaderMaterial(createSphereShader({ texture, radius: radiusWithOffset }))

          const geometry = texture && new THREE.SphereGeometry(radiusWithOffset, SPHERE_WIDTH, SPHERE_HEIGHT)

          geometry?.scale?.(-1, 1, 1)

          const mesh = textureMesh || new THREE.Mesh(geometry, material)

          textureMesh && mesh.scale.set(radiusWithOffset, radiusWithOffset, radiusWithOffset)

          layers[zoom] = {
            zoom,
            mesh,

            imageWidth,
            imageHeight,
            imageMaxWidth,
            imageMaxHeight
          }

          emit('onImageApplied', { imageWidth, imageHeight, imageMaxWidth, imageMaxHeight })
          emit('onMeshChanged', mesh)
        },

        onLayerSelected: ({ zoom }) => {
          Object.values(layers).forEach(({ zoom: x, mesh }) => x > zoom && state.scene.remove(mesh))

          const { mesh, imageWidth, imageHeight, imageMaxWidth, imageMaxHeight } = layers[zoom]

          mesh && state.scene.add(mesh)

          emit('onImageApplied', { imageWidth, imageHeight, imageMaxWidth, imageMaxHeight })
          emit('onMeshChanged', mesh)
        },

        onLayerLoaded: () => {
          tiler.applyZoom(MAX_ZOOM)
        },

        onTileLoading: x => onTileLoading?.(x)
      }))

      tiled && await tiler.applyImage(image)
      tiled && tiler.applyZoom(INITIAL_ZOOM, { ignore: true })

      return {
        ...state,
        lat,
        lon
      }
    },

    applyZoom(zoom, options) { tiler?.applyZoom?.(zoom, options) },
    applyOrientation(lon, lat) { tiler?.applyOrientation?.(lon, lat) },

    dispose() {
      state.scene && state.scene.remove.apply(state.scene, state.scene.children)

      Object.values(layers || {}).forEach(({ mesh }) => {
        mesh?.dispose?.()
      })

      layers = {}

      tiler?.dispose?.()
      tiler = null

      resizeObserver?.unobserve?.(container)
      resizeObserver = null

      Object.keys(state).map(k => {
        state[k]?.dispose?.()
        state[k] = null
      })
    },

    onImageApplied(x) {
      x && on('onImageApplied', x)
    },

    onMeshChanged(x) {
      x && on('onMeshChanged', x)
    },

    onZoomLoaded(x) {
      x && on('onZoomLoaded', x)
    }
  }
}
